import Group from 'components/utils/Group'
import NumberRating from 'components/Common/NumberRating'
import LoadingBox from 'components/Common/Loading/LoadingBox'
import Divider from 'LuxKit/Divider'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import styled from 'styled-components'
import { rem } from 'polished'
import {
  OfferDetailImageGallery,
  OfferDetailDescription,
  OfferDetailReviews,
  OfferDetailMap,
} from '../OfferDetail'
import type { OfferDetailReview, OfferDetailGeoLocation } from '../OfferDetail'
import type { ExperienceDetailResult, TripItineraryData } from 'tripPlanner/api/types'
import ExperienceDetailMetadata from './ExperienceDetailMetadata'
import ExperienceDetailPricing from './ExperienceDetailPricing'
import AddToTripAction from 'tripPlanner/components/GenericDrawer/AddToTripAction'

const ContentWrapper = styled.div`
  margin: 0 auto;
  padding: ${rem(24)};
  width: ${rem(700)};
  max-width: 100%;
  box-sizing: border-box;
`

const HeaderGroup = styled(Group)`
  flex: 1;
  min-width: 0;
`

const CategoryLabel = styled(BodyText)`
  text-transform: capitalize;
`

export type DrawerExperience =
  Omit<ExperienceDetailResult, 'images' | 'redemptionLocations' | 'geoLocation'> & {
    images: Array<App.Image>
    geoLocation?: OfferDetailGeoLocation
  }

interface Props {
  experience: DrawerExperience
  itineraryData?: TripItineraryData
  onSendMessage?: (message: string) => void
  reviews?: Array<OfferDetailReview>
  reviewsLoading?: boolean
}

function ExperienceDetailView({
  experience,
  itineraryData,
  onSendMessage,
  reviews,
  reviewsLoading,
}: Props) {
  const {
    offerId,
    name,
    location,
    description,
    images,
    rating,
    reviewsTotal,
    geoLocation,
    deeplinkUrl,
    price,
    value,
    currencyCode,
    duration,
    bookingType,
    categories,
    isSoldOut,
    expirationDate,
  } = experience

  const hasDescription = !!description
  const hasReviews = !!reviews && reviews.length > 0
  const hasGeoLocation = !!geoLocation
  const categoryLabels = categories.map((c) => c.label).join(', ')

  return (
    <ContentWrapper>
      <Group direction="vertical" gap={24}>
        <Group
          direction="horizontal"
          gap={16}
          horizontalAlign="space-between"
          verticalAlign="start"
        >
          <HeaderGroup direction="vertical" gap={12}>
            <Group direction="vertical">
              <Heading variant="heading2">{name}</Heading>
              <BodyText variant="medium" colour="neutral-two">{location}</BodyText>
              {categoryLabels && (
                <CategoryLabel variant="small" colour="neutral-three">{categoryLabels}</CategoryLabel>
              )}
            </Group>
            {rating !== undefined && rating > 0 && (
              <NumberRating
                rating={rating}
                total={reviewsTotal}
                variant="small"
              />
            )}
          </HeaderGroup>
          {itineraryData && onSendMessage && (
            <AddToTripAction
              itineraryData={itineraryData}
              onSendMessage={onSendMessage}
              placeName={name}
              offerId={offerId}
              offerType="experience"
              coordinates={geoLocation}
              variant="outline"
            />
          )}
        </Group>

        <OfferDetailImageGallery images={images} />

        <ExperienceDetailMetadata
          duration={duration}
          bookingType={bookingType}
          expirationDate={expirationDate}
        />

        {hasDescription && (
          <>
            <Divider kind="secondary" />
            <OfferDetailDescription description={description} />
          </>
        )}

        <Divider kind="secondary" />

        <ExperienceDetailPricing
          price={price}
          value={value}
          currencyCode={currencyCode}
          deeplinkUrl={deeplinkUrl}
          isSoldOut={isSoldOut}
        />

        {(hasReviews || reviewsLoading) && (
          <>
            <Divider kind="secondary" />
            {reviewsLoading && !hasReviews && (
              <Group direction="vertical" gap={12}>
                <LoadingBox height={20} width={120} borderRadius="S" />
                <LoadingBox height={60} width={320} borderRadius="S" />
              </Group>
            )}
            {hasReviews && (
              <OfferDetailReviews
                rating={rating}
                reviewsTotal={reviewsTotal}
                reviews={reviews}
              />
            )}
          </>
        )}

        {hasGeoLocation && (
          <>
            <Divider kind="secondary" />
            <OfferDetailMap
              geoLocation={geoLocation}
              offerName={name}
            />
          </>
        )}
      </Group>
    </ContentWrapper>
  )
}

export default ExperienceDetailView
